/**
 * @file useContextMenu.js
 * @description Context menu management for tree nodes
 *
 * Handles:
 * - Context menu positioning and visibility
 * - Menu options based on node type
 * - Menu action handling
 */

import { ref, computed } from 'vue';

export function useContextMenu() {
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * Context menu state
   * @type {Ref<Object>}
   */
  const contextMenu = ref({
    visible: false,
    x: 0,
    y: 0,
    node: null,
    type: null, // 'hierarchy' or 'electrical'
  });

  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================

  /**
   * Get context menu style for positioning
   */
  const contextMenuStyle = computed(() => {
    if (!contextMenu.value.visible) {
      return { display: 'none' };
    }

    return {
      position: 'fixed',
      left: `${contextMenu.value.x}px`,
      top: `${contextMenu.value.y}px`,
      zIndex: 9999,
    };
  });

  /**
   * Get available menu options for current node
   */
  const contextMenuOptions = computed(() => {
    if (!contextMenu.value.node) {return [];}

    const node = contextMenu.value.node;
    const options = [];

    // Common actions for all node types
    options.push({
      label: 'Refresh',
      icon: 'refresh',
      action: 'refresh',
    });

    // Node type specific actions
    switch (node.type) {
      case 'site':
        options.push(
          { separator: true },
          {
            label: 'Add Building',
            icon: 'add',
            action: 'add-building',
          },
          {
            label: 'Export TTL',
            icon: 'file_download',
            action: 'export-ttl',
          },
        );
        break;

      case 'building':
        options.push(
          { separator: true },
          {
            label: 'Add Level',
            icon: 'add',
            action: 'add-level',
          },
          {
            label: 'Delete Building',
            icon: 'delete',
            color: 'negative',
            action: 'delete',
          },
        );
        break;

      case 'level':
        options.push(
          { separator: true },
          {
            label: 'Add Space',
            icon: 'add',
            action: 'add-space',
          },
          {
            label: 'Delete Level',
            icon: 'delete',
            color: 'negative',
            action: 'delete',
          },
        );
        break;

      case 'space':
        options.push(
          { separator: true },
          {
            label: 'Add Equipment',
            icon: 'add',
            action: 'add-equipment',
          },
          {
            label: 'Delete Space',
            icon: 'delete',
            color: 'negative',
            action: 'delete',
          },
        );
        break;

      case 'equipment':
        options.push(
          { separator: true },
          {
            label: 'Add Device',
            icon: 'add',
            action: 'add-device',
          },
          {
            label: 'Add Point',
            icon: 'add_circle',
            action: 'add-point',
          },
        );

        // Template actions
        if (node.template_id) {
          options.push(
            { separator: true },
            {
              label: 'Sync with Template',
              icon: 'sync',
              action: 'sync-template',
            },
            {
              label: 'Detach from Template',
              icon: 'link_off',
              action: 'detach-template',
            },
          );
        } else {
          options.push(
            { separator: true },
            {
              label: 'Save as Template',
              icon: 'save',
              action: 'save-template',
            },
          );
        }

        // Power connection
        if (node.requiresPower) {
          options.push(
            { separator: true },
            {
              label: node.powerSourceId ? 'Change Power Source' : 'Connect Power',
              icon: 'power',
              action: 'connect-power',
            },
          );

          if (node.powerSourceId) {
            options.push({
              label: 'Disconnect Power',
              icon: 'power_off',
              color: 'warning',
              action: 'disconnect-power',
            });
          }
        }

        // DDC allocation
        if (node.isControllable) {
          options.push(
            { separator: true },
            {
              label: node.ddcControllerId ? 'Change DDC Controller' : 'Allocate to DDC',
              icon: 'developer_board',
              action: 'allocate-ddc',
            },
          );

          if (node.ddcControllerId) {
            options.push({
              label: 'Deallocate from DDC',
              icon: 'cancel',
              color: 'warning',
              action: 'deallocate-ddc',
            });
          }
        }

        options.push(
          { separator: true },
          {
            label: 'Edit Equipment',
            icon: 'edit',
            action: 'edit',
          },
          {
            label: 'Delete Equipment',
            icon: 'delete',
            color: 'negative',
            action: 'delete',
          },
        );
        break;

      case 'device':
        options.push(
          { separator: true },
          {
            label: 'Add Point',
            icon: 'add_circle',
            action: 'add-point',
          },
        );

        // Compound device actions
        if (node.isCompound) {
          options.push(
            { separator: true },
            {
              label: 'View Components',
              icon: 'account_tree',
              action: 'view-components',
            },
            {
              label: 'Disassemble Compound',
              icon: 'call_split',
              color: 'warning',
              action: 'disassemble-compound',
            },
          );
        }

        // Template actions
        if (node.template_id) {
          options.push(
            { separator: true },
            {
              label: 'Sync with Template',
              icon: 'sync',
              action: 'sync-template',
            },
            {
              label: 'Detach from Template',
              icon: 'link_off',
              action: 'detach-template',
            },
          );
        } else {
          options.push(
            { separator: true },
            {
              label: 'Save as Template',
              icon: 'save',
              action: 'save-template',
            },
          );
        }

        options.push(
          { separator: true },
          {
            label: 'Edit Device',
            icon: 'edit',
            action: 'edit',
          },
          {
            label: 'Delete Device',
            icon: 'delete',
            color: 'negative',
            action: 'delete',
          },
        );
        break;

      case 'point':
        options.push(
          { separator: true },
          {
            label: 'Edit Point',
            icon: 'edit',
            action: 'edit',
          },
          {
            label: 'Delete Point',
            icon: 'delete',
            color: 'negative',
            action: 'delete',
          },
        );
        break;

      // Electrical tree node types
      case 'main_switchboard':
      case 'mechanical_switchboard':
      case 'distribution_board':
      case 'ddc_panel':
        options.push(
          { separator: true },
          {
            label: 'Add Child Switchboard',
            icon: 'add',
            action: 'add-switchboard',
          },
          {
            label: 'Link Existing Switchboard',
            icon: 'link',
            action: 'link-switchboard',
          },
        );

        if (node.type === 'main_switchboard' || node.type === 'mechanical_switchboard') {
          options.push({
            label: 'Classify Switchboard',
            icon: 'category',
            action: 'classify-switchboard',
          });
        }

        options.push(
          { separator: true },
          {
            label: 'Edit',
            icon: 'edit',
            action: 'edit',
          },
          {
            label: 'Remove from Hierarchy',
            icon: 'remove_circle',
            color: 'warning',
            action: 'remove-from-tree',
          },
        );
        break;
    }

    return options;
  });

  // ============================================================================
  // METHODS
  // ============================================================================

  /**
   * Show context menu at mouse position
   * @param {MouseEvent} event - Mouse event
   * @param {Object} node - Tree node
   * @param {string} type - Tree type ('hierarchy' or 'electrical')
   */
  function showContextMenu(event, node, type = 'hierarchy') {
    event.preventDefault();
    event.stopPropagation();


    // Position menu at mouse
    contextMenu.value = {
      visible: true,
      x: event.clientX,
      y: event.clientY,
      node,
      type,
    };
  }

  /**
   * Hide context menu
   */
  function hideContextMenu() {
    contextMenu.value = {
      visible: false,
      x: 0,
      y: 0,
      node: null,
      type: null,
    };
  }

  /**
   * Handle context menu action selection
   * @param {string} action - Action identifier
   * @returns {Object} Action details
   */
  function handleContextMenuAction(action) {
    const node = contextMenu.value.node;
    const type = contextMenu.value.type;


    // Hide menu
    hideContextMenu();

    // Return action details for parent component to handle
    return {
      action,
      node,
      type,
    };
  }

  /**
   * Check if action is available for node
   * @param {Object} node - Tree node
   * @param {string} action - Action identifier
   * @returns {boolean}
   */
  function isActionAvailable(node, action) {
    const options = getMenuOptionsForNode(node);
    return options.some((opt) => opt.action === action);
  }

  /**
   * Get menu options for specific node (without computed dependency)
   * @param {Object} node - Tree node
   * @returns {Array<Object>}
   */
  function getMenuOptionsForNode(node) {
    // Same logic as contextMenuOptions computed, but as a function
    // This allows checking options without setting contextMenu state
    const options = [];

    switch (node.type) {
      case 'equipment':
        if (node.template_id) {
          options.push({ action: 'sync-template' }, { action: 'detach-template' });
        } else {
          options.push({ action: 'save-template' });
        }

        if (node.requiresPower) {
          options.push({ action: 'connect-power' });
          if (node.powerSourceId) {
            options.push({ action: 'disconnect-power' });
          }
        }

        if (node.isControllable) {
          options.push({ action: 'allocate-ddc' });
          if (node.ddcControllerId) {
            options.push({ action: 'deallocate-ddc' });
          }
        }
        break;

      case 'device':
        if (node.isCompound) {
          options.push({ action: 'view-components' }, { action: 'disassemble-compound' });
        }

        if (node.template_id) {
          options.push({ action: 'sync-template' }, { action: 'detach-template' });
        } else {
          options.push({ action: 'save-template' });
        }
        break;
    }

    // All nodes have edit and delete
    options.push({ action: 'edit' }, { action: 'delete' });

    return options;
  }

  /**
   * Build electrical tree context menu items
   * @param {Object} node - Tree node
   * @param {Object} actions - Action callbacks
   * @returns {Array<Object>} Menu items with action callbacks
   */
  function buildElectricalContextMenu(node, actions) {
    const items = [];
    const brickClass = node.brick_class || '';
    const equipmentData = node.data || {};

    // Add Existing Switchboard option (if applicable)
    if (actions.canAddSwitchboard && actions.canAddSwitchboard(node)) {
      items.push({
        label: 'Add Existing Switchboard',
        icon: 'add',
        action: () => actions.openAddExistingSwitchboardDialog(node),
      });
    }

    // DDC Controller Allocation (for equipment that isn't already a DDC panel)
    if (node.type === 'equipment' && !brickClass.includes('DDC_Panel')) {
      if (equipmentData.controller) {
        items.push({
          label: 'Change DDC Controller',
          icon: 'swap_horiz',
          action: () => actions.openDdcAllocationDialog(node),
        });
        items.push({
          label: 'Deallocate Controller',
          icon: 'link_off',
          action: () => actions.deallocateDdcController(node),
        });
      } else {
        items.push({
          label: 'Allocate DDC Controller',
          icon: 'memory',
          action: () => actions.openDdcAllocationDialog(node),
        });
      }
    }

    // Power Connection (for equipment)
    if (node.type === 'equipment') {
      if (equipmentData.power_source) {
        items.push({
          label: 'Change Power Source',
          icon: 'power',
          action: () => actions.openPowerConnectionDialog(node),
        });
        items.push({
          label: 'Disconnect Power',
          icon: 'power_off',
          action: () => actions.disconnectPower(node),
        });
      } else {
        items.push({
          label: 'Connect Power Source',
          icon: 'electrical_services',
          action: () => actions.openPowerConnectionDialog(node),
        });
      }
    }

    // Switchboard Classification (for MSSB only)
    if (brickClass.includes('Mechanical_Services_Switchboard')) {
      items.push({
        label: 'Set Classification',
        icon: 'label',
        action: () => actions.openSwitchboardClassificationDialog(node),
      });
    }

    // Separator before edit/view
    if (items.length > 0) {
      items.push({ separator: true });
    }

    // Edit option
    items.push({
      label: `Edit ${node.label}`,
      icon: 'edit',
      action: () => actions.editElectricalNode(node),
    });

    // View Details option
    items.push({
      label: 'View Details',
      icon: 'info',
      action: () => actions.viewElectricalDetails(node),
    });

    // Delete option (not for transformers)
    if (node.type !== 'transformer') {
      items.push({ separator: true });
      items.push({
        label: `Delete ${node.label}`,
        icon: 'delete',
        color: 'negative',
        action: () => actions.deleteElectricalNode(node),
      });
    }

    return items;
  }

  /**
   * Check if a node can have switchboards added
   * @param {Object} node - Tree node
   * @returns {boolean}
   */
  function canAddSwitchboard(node) {
    if (!node) {return false;}

    // Spaces can have switchboards
    if (node.type === 'space') {
      return true;
    }

    // Electrical equipment can have sub-switchboards
    const brickClass = node.brick_class || '';
    return brickClass.includes('Transformer') ||
           brickClass.includes('Main_Switchboard') ||
           brickClass.includes('Mechanical_Services_Switchboard');
  }

  // ============================================================================
  // RETURN API
  // ============================================================================

  return {
    // State
    contextMenu,

    // Computed
    contextMenuStyle,
    contextMenuOptions,

    // Methods
    showContextMenu,
    hideContextMenu,
    handleContextMenuAction,
    isActionAvailable,
    getMenuOptionsForNode,

    // Electrical tree helpers
    buildElectricalContextMenu,
    canAddSwitchboard,
  };
}
